/**
 * 월간 일정 (운영자용 달력)
 *
 * 한 화면에서 네 가지를 본다:
 *   - 공휴일(빨간날)  : 하루 전체 자율학습으로 처리되는 날 (실제 벌점 판정에 반영)
 *   - 수능·모의고사   : 본부가 등록, 모든 매장 공통
 *   - 학사 일정       : 방학·개학·시험기간 (본부 공통)
 *   - 매장 자체 일정   : 휴무일·행사 (매장이 직접 등록)
 *
 * ⚠️ index.css 의 .w-N/.h-N 이 폭·높이를 함께 강제하는 버그가 있어
 *    직사각형 요소에는 Tailwind 크기 클래스를 쓰지 않고 인라인 스타일을 쓴다.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../config/api';

interface CalendarEvent {
  id: string | null;
  category: 'holiday' | 'exam' | 'academic' | 'store';
  title: string;
  memo?: string | null;
  start_date: string;
  end_date?: string | null;
  scope: 'holiday' | 'global' | 'organization';
  editable: boolean;
}

const CATEGORY_STYLE: Record<string, { label: string; chip: string; dot: string }> = {
  holiday: { label: '공휴일', chip: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
  exam: { label: '수능·모의고사', chip: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500' },
  academic: { label: '학사 일정', chip: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  store: { label: '매장 일정', chip: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
};

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 지금은 **보기 전용**으로 배포한다.
 * 등록·수정·삭제 API 는 이미 동작하지만(검증 완료), 운영 정책이 확정될 때까지
 * 화면에서는 "준비 중" 안내만 띄운다. 열려면 이 값만 false 로 바꾸면 된다.
 */
const READ_ONLY = true;

const apiCall = async <T,>(url: string, options: RequestInit = {}): Promise<T> => {
  const token = localStorage.getItem('accessToken') || 'demo-token';
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  return response.json();
};

const toKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const MonthlyCalendarPage: React.FC = () => {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(toKey(today));

  // 일정 추가 폼
  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState<'store' | 'exam' | 'academic'>('store');
  const [newStartDate, setNewStartDate] = useState<string>(toKey(today));
  const [newEndDate, setNewEndDate] = useState('');

  // 휴일 지정/해제 — window.prompt/confirm 은 환경에 따라 차단되어
  // "눌러도 반응 없음"으로 보인다 → 화면 안에서 입력받는다
  const [holidayMode, setHolidayMode] = useState<'none' | 'add' | 'remove'>('none');
  const [holidayName, setHolidayName] = useState('매장 휴일');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [comingSoon, setComingSoon] = useState(false);

  // 보기 전용 상태에서 등록·수정 시도 → 안내만
  const showComingSoon = () => {
    setComingSoon(true);
    setHolidayMode('none');
    setConfirmDeleteId(null);
  };

  // 수능·학사 일정은 본부만 등록 가능 → 점주에게는 아예 못 고르게 막는다
  //   (고른 뒤 403 을 보는 것보다 낫다)
  const isHq = (() => {
    try {
      const raw = localStorage.getItem('user');
      const role = raw ? JSON.parse(raw)?.role : null;
      return role === 'hq_admin' || role === 'admin';
    } catch {
      return false;
    }
  })();

  const year = cursor.getFullYear();
  const month = cursor.getMonth() + 1;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall<CalendarEvent[]>(
        `/api/v1/owner/calendar?year=${year}&month=${month}`
      );
      setEvents(data);
    } catch (e) {
      console.error('일정 조회 실패', e);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  // 날짜별 일정 묶음 (기간 일정은 걸치는 모든 날에 표시)
  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const ev of events) {
      const start = new Date(`${ev.start_date}T00:00:00`);
      const end = ev.end_date ? new Date(`${ev.end_date}T00:00:00`) : start;
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = toKey(d);
        (map[key] ||= []).push(ev);
      }
    }
    return map;
  }, [events]);

  // 달력 격자 (앞뒤 달 채워서 6주)
  const cells = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const startOffset = first.getDay();
    const gridStart = new Date(year, month - 1, 1 - startOffset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      return d;
    });
  }, [year, month]);

  const moveMonth = (delta: number) => {
    const next = new Date(year, month - 1 + delta, 1);
    setCursor(next);
    // 선택 날짜가 다른 달에 남아 있으면 "8월 보면서 7월에 등록"되는 사고가 난다 → 1일로 이동
    const firstKey = toKey(next);
    setSelectedDate(firstKey);
    setNewStartDate(firstKey);
  };

  const selectedEvents = eventsByDate[selectedDate] || [];
  const selectedIsHoliday = selectedEvents.some((e) => e.category === 'holiday');

  const handleAdd = async () => {
    if (READ_ONLY) return showComingSoon();
    if (!newTitle.trim()) {
      alert('일정 이름을 입력해주세요.');
      return;
    }
    try {
      await apiCall('/api/v1/owner/calendar', {
        method: 'POST',
        body: JSON.stringify({
          title: newTitle.trim(),
          category: newCategory,
          start_date: newStartDate || selectedDate,
          end_date: newEndDate || null,
          scope: newCategory === 'store' ? 'organization' : 'global',
        }),
      });
      setNewTitle('');
      setNewEndDate('');
      await load();
      setSelectedDate(newStartDate || selectedDate);
    } catch (e) {
      alert(`일정 등록 실패: ${e instanceof Error ? e.message : e}`);
    }
  };

  const handleDelete = async (ev: CalendarEvent) => {
    if (READ_ONLY) return showComingSoon();
    if (!ev.id) return;
    try {
      await apiCall(`/api/v1/owner/calendar/${ev.id}`, { method: 'DELETE' });
      setConfirmDeleteId(null);
      await load();
    } catch (e) {
      alert(`삭제 실패: ${e instanceof Error ? e.message : e}`);
    }
  };

  // 공휴일 지정/해제 — 자율학습 판정에 실제로 영향을 준다
  const applyHoliday = async (isHoliday: boolean, name: string) => {
    if (READ_ONLY) return showComingSoon();
    try {
      await apiCall('/api/v1/owner/holidays', {
        method: 'POST',
        body: JSON.stringify({
          holiday_date: selectedDate,
          name: isHoliday ? name || '매장 휴일' : '정상 운영',
          is_holiday: isHoliday,
        }),
      });
      setHolidayMode('none');
      setHolidayName('매장 휴일');
      await load();
    } catch (e) {
      alert(`변경 실패: ${e instanceof Error ? e.message : e}`);
    }
  };

  return (
    <div className="p-6 space-y-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold">월간 일정</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => moveMonth(-1)} className="p-2 border rounded hover:bg-gray-50" title="이전 달">
            <ChevronLeftIcon style={{ width: '1rem', height: '1rem' }} />
          </button>
          <span className="text-lg font-semibold min-w-[7rem] text-center">
            {year}년 {month}월
          </span>
          <button onClick={() => moveMonth(1)} className="p-2 border rounded hover:bg-gray-50" title="다음 달">
            <ChevronRightIcon style={{ width: '1rem', height: '1rem' }} />
          </button>
          <button
            onClick={() => {
              setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
              setSelectedDate(toKey(today));
              setNewStartDate(toKey(today));
            }}
            className="ml-1 px-3 py-2 text-sm border rounded hover:bg-gray-50"
          >
            오늘
          </button>
        </div>
      </div>

      {READ_ONLY && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
          🚧 지금은 <strong>보기 전용</strong>입니다. 일정·휴일 <strong>등록과 수정은 준비 중</strong>이며,
          공휴일·수능 등 등록된 일정 확인만 가능합니다.
        </div>
      )}

      {/* 범례 */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
        {Object.entries(CATEGORY_STYLE).map(([key, s]) => (
          <span key={key} className="flex items-center gap-1.5">
            <span className={`rounded-full ${s.dot}`} style={{ width: '0.6rem', height: '0.6rem' }} />
            {s.label}
          </span>
        ))}
        <span className="text-xs text-gray-500">
          ※ 공휴일로 지정된 날만 <strong>하루 전체 자율학습</strong>으로 처리됩니다.
        </span>
      </div>

      {/* 달력 */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 bg-gray-50 border-b">
          {WEEKDAYS.map((w, i) => (
            <div
              key={w}
              className={`px-2 py-2 text-center text-sm font-medium ${
                i === 0 ? 'text-rose-600' : i === 6 ? 'text-blue-600' : 'text-gray-600'
              }`}
            >
              {w}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {cells.map((d) => {
            const key = toKey(d);
            const inMonth = d.getMonth() === month - 1;
            const isToday = key === toKey(today);
            const isSelected = key === selectedDate;
            const dayEvents = eventsByDate[key] || [];
            const isHoliday = dayEvents.some((e) => e.category === 'holiday');
            const isSunday = d.getDay() === 0;

            return (
              <button
                key={key}
                onClick={() => { setSelectedDate(key); setNewStartDate(key); setHolidayMode('none'); }}
                className={`text-left border-b border-r p-1.5 align-top transition-colors ${
                  inMonth ? 'bg-white' : 'bg-gray-50'
                } ${isSelected ? 'ring-2 ring-inset ring-blue-500' : 'hover:bg-blue-50'}`}
                style={{ minHeight: '5.5rem' }}
              >
                <div className="flex items-center gap-1">
                  <span
                    className={`text-sm ${
                      !inMonth
                        ? 'text-gray-300'
                        : isHoliday || isSunday
                        ? 'text-rose-600 font-semibold'
                        : d.getDay() === 6
                        ? 'text-blue-600'
                        : 'text-gray-800'
                    }`}
                  >
                    {d.getDate()}
                  </span>
                  {isToday && (
                    <span className="px-1 text-[0.6rem] bg-blue-600 text-white rounded">오늘</span>
                  )}
                </div>

                <div className="mt-1 space-y-0.5">
                  {dayEvents.slice(0, 3).map((ev, idx) => (
                    <div
                      key={`${ev.id || ev.title}-${idx}`}
                      className={`px-1 py-0.5 text-[0.65rem] rounded truncate ${CATEGORY_STYLE[ev.category]?.chip || ''}`}
                      title={ev.title}
                    >
                      {ev.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[0.65rem] text-gray-500 px-1">+{dayEvents.length - 3}건</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {loading && <p className="text-sm text-gray-500">불러오는 중…</p>}

      {/* 선택한 날 상세 */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-semibold text-gray-900">
            {selectedDate}
            {selectedIsHoliday && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-rose-100 text-rose-700 rounded">
                공휴일 · 하루 전체 자율학습
              </span>
            )}
          </h3>
          <button
            onClick={() =>
              READ_ONLY ? showComingSoon() : setHolidayMode(selectedIsHoliday ? 'remove' : 'add')
            }
            className={`px-3 py-1.5 text-sm rounded ${
              selectedIsHoliday
                ? 'border text-gray-700 hover:bg-gray-50'
                : 'bg-rose-600 text-white hover:bg-rose-700'
            }`}
          >
            {selectedIsHoliday ? '정상 운영일로 되돌리기' : '이 날을 휴일로 지정'}
          </button>
        </div>

        {comingSoon && (
          <div className="flex items-center justify-between gap-2 mb-3 p-3 bg-amber-50 border border-amber-200 rounded">
            <span className="text-sm text-amber-900">
              🚧 <strong>준비 중인 기능입니다.</strong> 일정·휴일 등록과 수정은 곧 열릴 예정입니다.
            </span>
            <button
              onClick={() => setComingSoon(false)}
              className="px-2 py-0.5 text-xs border rounded hover:bg-white shrink-0"
            >
              닫기
            </button>
          </div>
        )}

        {holidayMode === 'add' && (
          <div className="flex flex-wrap items-center gap-2 mb-3 p-3 bg-rose-50 border border-rose-200 rounded">
            <span className="text-sm text-rose-800">
              <strong>{selectedDate}</strong> 을(를) 휴일로 지정합니다 (하루 전체 자율학습)
            </span>
            <input
              type="text"
              value={holidayName}
              onChange={(e) => setHolidayName(e.target.value)}
              placeholder="휴일 이름 (예: 창립기념일)"
              className="border rounded px-2 py-1 text-sm flex-1 min-w-[10rem]"
            />
            <button
              onClick={() => applyHoliday(true, holidayName)}
              className="px-3 py-1 bg-rose-600 text-white rounded text-sm hover:bg-rose-700"
            >
              휴일로 지정
            </button>
            <button
              onClick={() => setHolidayMode('none')}
              className="px-3 py-1 border rounded text-sm hover:bg-gray-50"
            >
              취소
            </button>
          </div>
        )}

        {holidayMode === 'remove' && (
          <div className="flex flex-wrap items-center gap-2 mb-3 p-3 bg-gray-50 border rounded">
            <span className="text-sm text-gray-700">
              <strong>{selectedDate}</strong> 을(를) 정상 운영일로 바꿉니다. 이 날은 평소처럼
              교시별 출결이 적용됩니다.
            </span>
            <button
              onClick={() => applyHoliday(false, '')}
              className="px-3 py-1 bg-gray-800 text-white rounded text-sm hover:bg-black"
            >
              정상 운영일로
            </button>
            <button
              onClick={() => setHolidayMode('none')}
              className="px-3 py-1 border rounded text-sm hover:bg-gray-50"
            >
              취소
            </button>
          </div>
        )}

        {selectedEvents.length === 0 ? (
          <p className="text-sm text-gray-500 py-2">등록된 일정이 없습니다.</p>
        ) : (
          <ul className="space-y-1.5 mb-3">
            {selectedEvents.map((ev, idx) => (
              <li
                key={`${ev.id || ev.title}-${idx}`}
                className="flex items-center justify-between gap-2 px-3 py-2 border rounded"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className={`px-1.5 py-0.5 text-xs rounded ${CATEGORY_STYLE[ev.category]?.chip || ''}`}>
                    {CATEGORY_STYLE[ev.category]?.label || ev.category}
                  </span>
                  <span className="truncate">{ev.title}</span>
                  {ev.end_date && ev.end_date !== ev.start_date && (
                    <span className="text-xs text-gray-500 shrink-0">
                      ({ev.start_date} ~ {ev.end_date})
                    </span>
                  )}
                </span>
                {ev.id && ev.editable && (
                  confirmDeleteId === ev.id ? (
                    <span className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => handleDelete(ev)}
                        className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        삭제
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50"
                      >
                        취소
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={() => (READ_ONLY ? showComingSoon() : setConfirmDeleteId(ev.id!))}
                      className="text-gray-400 hover:text-red-600 shrink-0"
                      title="삭제"
                    >
                      <TrashIcon style={{ width: '0.9rem', height: '0.9rem' }} />
                    </button>
                  )
                )}
              </li>
            ))}
          </ul>
        )}

        {/* 일정 추가 */}
        <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded">
          <label className="text-sm text-gray-600">
            날짜
            <input
              type="date"
              value={newStartDate}
              onChange={(e) => setNewStartDate(e.target.value)}
              className="ml-2 border rounded px-2 py-1.5 text-sm"
            />
          </label>
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value as typeof newCategory)}
            className="border rounded px-2 py-1.5 text-sm"
          >
            <option value="store">매장 일정</option>
            {/* 수능·학사는 본부 공통 일정 → 점주는 선택 불가 */}
            <option value="exam" disabled={!isHq}>
              수능·모의고사{!isHq && ' (본부 전용)'}
            </option>
            <option value="academic" disabled={!isHq}>
              학사 일정{!isHq && ' (본부 전용)'}
            </option>
          </select>
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="일정 이름 (예: 여름방학 시작)"
            className="border rounded px-2 py-1.5 text-sm flex-1 min-w-[12rem]"
          />
          <label className="text-sm text-gray-600">
            종료일(선택)
            <input
              type="date"
              value={newEndDate}
              onChange={(e) => setNewEndDate(e.target.value)}
              className="ml-2 border rounded px-2 py-1.5 text-sm"
            />
          </label>
          <button
            onClick={handleAdd}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            <PlusIcon style={{ width: '0.9rem', height: '0.9rem' }} />
            추가
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          ※ 위 <strong>날짜</strong>에 등록됩니다. 달력에서 날짜를 클릭해도 바뀝니다.
          <br />
          ※ 수능·모의고사·학사 일정은 본부 관리자만 등록할 수 있습니다 (모든 매장에 공통으로 표시됨).
        </p>
      </div>
    </div>
  );
};

export default MonthlyCalendarPage;
